'use client';
import type { ErrorInfo, ReactElement, ReactNode } from 'react';
import { cloneElement, Component } from 'react';

interface Props {
  children?: ReactNode;
  fallback: ReactElement<{
    resetBoundary: () => void;
    error: Error | null;
  }>;
  resetQuery?: () => void;
  onError?: (error: Error) => void;
  /**
   * Values the subtree's data depends on, read like `useEffect` deps. Without them a caught error
   * clears only on `resetBoundary`, so a panel that failed under one identity keeps its fallback
   * after the identity changes.
   */
  resetKeys?: readonly unknown[];
  /**
   * Errors it accepts are expected states the fallback already explains, so they reach `onError`
   * but are not logged as uncaught.
   */
  isExpectedError?: (error: Error) => boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

const initialState: State = { hasError: false, error: null };

const haveResetKeysChanged = (previous: readonly unknown[] = [], next: readonly unknown[] = []) =>
  previous.length !== next.length || next.some((key, index) => !Object.is(key, previous[index]));

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = initialState;
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error: error };
  }
  resetBoundary = () => {
    this.props.resetQuery?.();
    this.setState(initialState);
  };

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.props.onError?.(error);
    if (this.props.isExpectedError?.(error)) {
      return;
    }
    console.error('Uncaught error:', error, errorInfo);
  }

  public componentDidUpdate(previousProps: Props) {
    if (!this.state.hasError) {
      return;
    }
    if (haveResetKeysChanged(previousProps.resetKeys, this.props.resetKeys)) {
      this.resetBoundary();
    }
  }

  public render() {
    if (this.state.hasError) {
      return cloneElement(this.props.fallback, {
        resetBoundary: this.resetBoundary,
        error: this.state.error,
      });
    }

    return this.props.children;
  }
}
